/**
 * §software-updates — product release notes ("What's New").
 *
 * XPAND ships as one shared image, so there is no per-tenant version or
 * "install update" button. Mission Control PUBLISHES curated release notes;
 * every tenant dashboard CONSUMES the same published feed + the real deployed
 * version. This is the genuine link between the master console and tenants.
 */
import { api } from "@/lib/api";

export type ReleaseCategory = "added" | "changed" | "improved" | "fixed" | "security";

export interface ReleaseItem {
  category: ReleaseCategory;
  text: string;
}

export interface ReleaseNote {
  id: string;
  version: string;
  title: string;
  summary: string;
  items: ReleaseItem[];
  published: boolean;
  publishedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface ReleaseVersion {
  shortSha: string;
  branch: string;
  buildTime: string;
  deployedAt: number | null;
  hasBuildMarker: boolean;
}

export interface TenantReleaseNotes {
  notes: ReleaseNote[];
  version: ReleaseVersion;
}

/** Tenant-facing: published notes + the current deployed version. */
export async function getReleaseNotes(): Promise<TenantReleaseNotes> {
  return api<TenantReleaseNotes>("/api/release-notes");
}

// ── Mission Control (master) publisher ───────────────────────────────────────
export async function listReleaseNotes(): Promise<{ notes: ReleaseNote[]; categories: ReleaseCategory[] }> {
  return api<{ notes: ReleaseNote[]; categories: ReleaseCategory[] }>("/api/master/release-notes");
}

export async function createReleaseNote(input: {
  version: string; title: string; summary?: string;
  items?: ReleaseItem[]; publish?: boolean;
}): Promise<{ note: ReleaseNote }> {
  return api<{ note: ReleaseNote }>("/api/master/release-notes", { method: "POST", body: input });
}

export async function updateReleaseNote(
  id: string,
  patch: Partial<{ version: string; title: string; summary: string; items: ReleaseItem[]; published: boolean }>,
): Promise<{ note: ReleaseNote }> {
  return api<{ note: ReleaseNote }>(`/api/master/release-notes/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}

export async function deleteReleaseNote(id: string): Promise<{ ok: boolean }> {
  return api<{ ok: boolean }>(`/api/master/release-notes/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export const CATEGORY_META: Record<ReleaseCategory, { label: string; color: string }> = {
  added: { label: "New", color: "oklch(72% 0.13 150)" },
  improved: { label: "Improved", color: "oklch(72% 0.12 240)" },
  changed: { label: "Changed", color: "oklch(74% 0.11 270)" },
  fixed: { label: "Fixed", color: "oklch(72% 0.10 200)" },
  security: { label: "Security", color: "oklch(72% 0.14 30)" },
};
